import { useEffect, useContext, useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useSurveyStore } from 'Store/Survey'
import { useAuthStore } from 'Store/Auth'
import { useProjectStore } from 'Store/Project'
import { TableContext } from 'Context/Table'

import StatusCell from './Components/StatusCell'
import ActionCell from './Components/ActionCell'
import moment from 'moment'
import 'moment-timezone'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'

import s2ab from 'Utils/s2ab'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'

const INITIAL_FILTER = {
  page: 1,
  pageSize: 10,
  division: '',
  startJobDate: '',
  endJobDate: '',
  customerTitle: '',
  saleTitle: '',
  contactPersonFullName: '',
  saleOrderId: '',
  jobStatus: '',
  projectId: '',
  noSaleOrder: undefined,
}

const ViewModel = () => {
  const navigate = useNavigate()
  const {
    data,
    totalData,
    siteManagers,
    getAllPagination,
    getSiteManagers,
    deleteSurvey,
    createProject,
    downloadPdf,
    approveSurvey,
    rejectSurvey,
    downloadApproverSignature,
    uploadApproverSignature,
    updateSurvey,
    deleteAll,
  } = useSurveyStore(
    state => ({
      data: state.data,
      totalData: state.totalData,
      siteManagers: state.siteManagers,
      getAllPagination: state.getAllPagination,
      getSiteManagers: state.getSiteManagers,
      deleteSurvey: state.delete,
      createProject: state.createProject,
      downloadPdf: state.downloadPdf,
      approveSurvey: state.approve,
      rejectSurvey: state.reject,
      downloadApproverSignature: state.downloadApproverSignature,
      uploadApproverSignature: state.uploadApproverSignature,
      updateSurvey: state.edit,
      deleteAll: state.deleteAll,
    }),
    shallow
  )
  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const { projects, getAllProject } = useProjectStore(
    state => ({
      projects: state.data,
      getAllProject: state.getAll,
    }),
    shallow
  )
  const {
    // currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateDefaultSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null)
  const [isOpenAssignSalesModal, setIsOpenAssignSalesModal] = useState(false)
  const [multipleSelectedSurveyIds, setMultipleSelectedSurveyIds] = useState<number[]>([])

  const userRoleId = profile?.roleId

  const isHideWritingAction = [2, 10].includes(userRoleId)

  const setupTable = () => {
    updateDefaultSorting('id', true)
    updatePagination(true)
  }

  const fetchMasterData = () => {
    getAllSaleOrder()
    getAllProject()
    getSiteManagers()
  }

  const fetchData = async (nextFilter?: typeof INITIAL_FILTER) => {
    const currentFilter = nextFilter ?? filter

    try {
      const { projectId, ...restFilter } = currentFilter
      const finaleFilter = {
        ...restFilter,
        projectID: projectId,
        startJobDate: moment(currentFilter.startJobDate).isValid()
          ? moment(currentFilter.startJobDate).format('YYYY-MM-DD')
          : undefined,
        endJobDate: moment(currentFilter.endJobDate).isValid()
          ? moment(currentFilter.endJobDate).format('YYYY-MM-DD')
          : undefined,
      }

      updateLoading(true)
      const { success, data: responseData } = await getAllPagination(finaleFilter)
      if (!success) {
        updateError(true)
      } else {
        onChangeFilter('page', responseData?.page ?? currentFilter.page)
        updateError(false)
      }

      updateLoading(false)
    } catch (err: any) {
      updateLoading(false)
      updateError(true)
    }
  }

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const submitFilter = async () => {
    const nextFilter = {
      ...filter,
      page: 1,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const nextPageDesktop = async (page: number) => {
    const nextFilter = {
      ...filter,
      page,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    setMultipleSelectedSurveyIds([])
    await fetchData(INITIAL_FILTER)
  }

  const addSurvey = () => {
    navigate('/survey/create')
  }

  const viewSurvey = (id: string) => {
    navigate(`/survey/${id}?view=1`)
  }

  const editSurvey = (id: string) => {
    navigate(`/survey/${id}`)
  }

  const askDeleteSurvey = async (id: string) => {
    const { isConfirmed } = await Swal.fire({
      title: 'ลบแบบสำรวจ?',
      text: 'คุณต้องการที่จะลบแบบสำรวจนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const { success, data } = await deleteSurvey(parseInt(id))
      if (success) {
        toast.success('Delete survey success')
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const onViewProject = (id: string) => {
    navigate(`/project/${id}`)
  }

  const onStartCreateProject = async (id: any) => {
    const project = data.find(item => item.id === parseInt(id))
    if (project?.isCreatedProject) {
      toast.info('แบบสำรวจนี้ได้สร้างโครงการไปแล้ว')
      return
    }

    console.log('project', project)

    withReactContent(Swal)
      .fire({
        title: 'สร้างโครงการ',
        html: (
          <Fragment>
            <div className='text-danger mb-3 font-weight-bold'>
              ประเภทโครงการที่กำลังจะสร้าง :{' '}
              {project?.shippingOnly ? 'จัดส่งเพียงอย่างเดียว' : 'ตอกเสาเข็ม'}
            </div>
            <div>กรุณาเลือกผู้จัดการสนาม (Project) ที่ต้องการมอบหมายให้กับโครงการนี้</div>
          </Fragment>
        ),
        input: 'select',
        inputOptions: siteManagers.reduce((options, manager) => {
          options[manager.id] = manager.fullName
          return options
        }, {}),
        inputPlaceholder:
          siteManagers.length === 0
            ? 'ไม่พบผู้จัดการสนาม (Project) ในระบบ'
            : 'เลือกผู้จัดการสนาม (Project)',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        preConfirm: async selectedManagerId => {
          if (!selectedManagerId) {
            Swal.showValidationMessage('กรุณาเลือกผู้จัดการสนาม')
            return
          }
          return selectedManagerId
        },
      })
      .then(async result => {
        if (result.isConfirmed && result.value) {
          const formData = new FormData()
          formData.append('siteManagerUserId', result.value)
          const { success, data } = await createProject(parseInt(id), formData)
          if (success) {
            toast.success('สร้างโครงการสำเร็จ')
            fetchData()
            // navigate(`/project/${data.projectId}`)
          } else {
            toast.error(data)
          }
        }
        return null
      })
  }

  // const askCreateProject = async (id: string) => {
  //   const { isConfirmed } = await Swal.fire({
  //     title: 'สร้างโครงการจากแบบสำรวจ',
  //     text: 'คุณต้องการที่จะสร้างโครงการจากแบบสำรวจนี้ใช่หรือไม่?',
  //     icon: 'warning',
  //     showCancelButton: true,
  //     confirmButtonText: 'ยืนยัน',
  //     cancelButtonText: 'ยกเลิก',
  //   })
  //   if (isConfirmed) {
  //     const { success, data } = await createProject(parseInt(id))
  //     if (success) {
  //       toast.success('สร้างโครงการสำเร็จ')
  //       // navigate(`/project/${data.projectId}`)
  //     } else {
  //       toast.error(data)
  //     }
  //   }
  // }

  const exportData = () => {
    const exportedDatas = [
      ['No', 'ชื่อหน่วยงาน', 'ชื่อลูกค้า', 'วันที่เริ่มงาน', 'วันที่สิ้นสุดงาน', 'สถานะงาน'],
    ]

    for (const item of data) {
      exportedDatas.push([
        item.id,
        item.division,
        item.customerTitle,
        moment(item.startJobDate).isValid()
          ? moment.utc(item.startJobDate).format('DD/MM/YYYY')
          : '-',
        moment(item.endJobDate).isValid() ? moment.utc(item.endJobDate).format('DD/MM/YYYY') : '-',
        item.jobStatus ?? '-',
      ])
    }

    const wb = XLSX.utils.book_new()
    wb.Props = {
      Title: `Survey List`,
      Subject: 'List',
      Author: 'Project Management System',
      CreatedDate: new Date(),
    }
    wb.SheetNames.push('List')

    const ws = XLSX.utils.aoa_to_sheet(exportedDatas)
    ws['!cols'] = [
      { width: 20 },
      { width: 30 },
      { width: 30 },
      { width: 30 },
      { width: 30 },
      { width: 20 },
    ]
    wb.Sheets['List'] = ws
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

    saveAs(new Blob([s2ab(wbout)]), 'survey.xlsx')
  }

  const onApprove = async (id: string) => {
    Swal.fire({
      title: 'อนุมัติแบบสำรวจ?',
      text: 'คุณต้องการที่จะอนุมัติแบบสำรวจนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async result => {
      if (result.isConfirmed) {
        const { success, data } = await approveSurvey(parseInt(id))
        if (success) {
          toast.success('อนุมัติแบบสำรวจสำเร็จ')
          fetchData()
        } else {
          toast.error(data)
        }
      }
    })
  }

  const onReject = async (id: string) => {
    Swal.fire({
      title: 'ไม่อนุมัติแบบสำรวจ?',
      text: 'คุณต้องการที่จะไม่อนุมัติแบบสำรวจนี้ใช่หรือไม่?',
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'กรุณาระบุเหตุผลในการไม่อนุมัติแบบสำรวจ',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      preConfirm: async reason => {
        if (!reason) {
          Swal.showValidationMessage('กรุณาระบุเหตุผลในการไม่อนุมัติแบบสำรวจ')
          return
        }
        return reason
      },
    }).then(async result => {
      if (result.isConfirmed && result.value) {
        const formData = new FormData()
        formData.append('reason', result.value)
        const { success, data } = await rejectSurvey(parseInt(id), formData)
        if (success) {
          toast.success('ไม่อนุมัติแบบสำรวจสำเร็จ')
          fetchData()
        } else {
          toast.error(data)
        }
      }
    })
  }

  const onDownloadPdf = async (id: any) => {
    if (id) {
      toast.info('กำลังดาวน์โหลดไฟล์ PDF กรุณารอสักครู่...')
      const { success, data } = await downloadPdf(parseInt(id))
      if (!success) {
        toast.error(data)
      } else {
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `survey_${id}.pdf`)
        document.body.appendChild(link)
        link.click()
      }
    }
  }

  const onDownloadApproverSignature = async (id: any) => {
    if (id) {
      const { success, data } = await downloadApproverSignature(parseInt(id))
      if (!success) {
        toast.error(data)
      } else {
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `approver_signature_survey_${id}.png`)
        document.body.appendChild(link)
        link.click()
      }
    }
  }

  const onUploadApproverSignature = async (id: any) => {
    Swal.fire({
      title: 'อัพโหลดลายเซ็นผู้อนุมัติ',
      text: 'กรุณาเลือกไฟล์ลายเซ็นผู้อนุมัติ (ไฟล์ต้องเป็นนามสกุล .png หรือ .jpg เท่านั้น)',
      input: 'file',
      inputAttributes: {
        accept: '.png, .jpg, .jpeg',
        'aria-label': 'Upload your profile picture',
      },
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      preConfirm: async file => {
        if (!file) {
          Swal.showValidationMessage('กรุณาเลือกไฟล์ลายเซ็นผู้อนุมัติ')
          return
        }
        const validTypes = ['image/png', 'image/jpg', 'image/jpeg']
        if (!validTypes.includes(file.type)) {
          Swal.showValidationMessage(
            'ไฟล์ลายเซ็นผู้อนุมัติจะต้องเป็นไฟล์นามสกุล .png หรือ .jpg เท่านั้น'
          )
          return
        }
        if (file.size > 2 * 1024 * 1024) {
          Swal.showValidationMessage('ไฟล์ลายเซ็นผู้อนุมัติจะต้องมีขนาดไม่เกิน 2MB')
          return
        }
        return file
      },
    }).then(async result => {
      if (result.isConfirmed && result.value) {
        const formData = new FormData()
        formData.append('signatureFile', result.value)
        const { success, data } = await uploadApproverSignature(parseInt(id), formData)
        if (success) {
          toast.success('อัพโหลดลายเซ็นผู้อนุมัติสำเร็จ')
          fetchData()
        } else {
          toast.error(data)
        }
      }
      return null
    })
  }

  const onEditNoteOther = (id: any) => {
    const finded = data.find((item: any) => item.id === id)
    if (finded) {
      Swal.fire({
        title: 'แก้ไขเรื่องอื่นๆ ที่จะต้องจัดเตรียมเพิ่มเติม ,ข้อคิดเห็น ,ข้อเสนอแนะ:',
        input: 'textarea',
        inputLabel: 'หมายเหตุอื่นๆ',
        inputValue: finded.noteOther || '',
        showCancelButton: true,
        confirmButtonText: 'บันทึก',
        cancelButtonText: 'ยกเลิก',
        preConfirm: async noteOther => {
          return noteOther
        },
      }).then(async result => {
        if (result.isConfirmed) {
          const { success, data } = await updateSurvey(parseInt(id), {
            noteOther: result.value || '',
          })
          if (success) {
            toast.success('แก้ไขหมายเหตุอื่นๆ สำเร็จ')
            fetchData()
          } else {
            toast.error(data)
          }
        }
      })
    }
  }

  const onAssignSales = (surveyId: number) => {
    setSelectedSurveyId(surveyId)
    setIsOpenAssignSalesModal(true)
  }

  const onCancelAssignSales = () => {
    setSelectedSurveyId(null)
    setIsOpenAssignSalesModal(false)
  }

  const onDeleteMultipleSurveys = async () => {
    if (multipleSelectedSurveyIds.length === 0) {
      toast.info('กรุณาเลือกแบบสำรวจที่ต้องการลบอย่างน้อย 1 รายการ')
      return
    }

    const { isConfirmed } = await Swal.fire({
      title: 'ลบแบบสำรวจที่เลือก?',
      text: `คุณต้องการที่จะลบแบบสำรวจที่เลือกทั้งหมด ${multipleSelectedSurveyIds.length} รายการใช่หรือไม่?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const formData = new FormData()
      multipleSelectedSurveyIds.forEach(id => {
        formData.append('ids[]', id.toString())
      })
      const { success, data } = await deleteAll(multipleSelectedSurveyIds)
      if (success) {
        toast.success('Delete surveys success')
        setMultipleSelectedSurveyIds([])
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: (
        <div
          className='form-check form-check-custom justify-content-center cursor-pointer'
          style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
          <input
            type='checkbox'
            className='form-check-input'
            checked={data.length > 0 && multipleSelectedSurveyIds.length === data.length}
            onChange={e => {
              const isChecked = e.target.checked
              if (isChecked) {
                setMultipleSelectedSurveyIds(data.map(item => item.id))
              } else {
                setMultipleSelectedSurveyIds([])
              }
            }}
          />
        </div>
      ),
      accessor: 'select',
      Cell: ({ row }: any) => {
        return (
          <div
            className='form-check form-check-custom justify-content-center cursor-pointer'
            style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
            <input
              type='checkbox'
              className='form-check-input'
              checked={multipleSelectedSurveyIds.includes(row.original.id)}
              onChange={e => {
                const isChecked = e.target.checked
                setMultipleSelectedSurveyIds(prev => {
                  if (isChecked) {
                    return [...prev, row.original.id]
                  } else {
                    return prev.filter(id => id !== row.original.id)
                  }
                })
              }}
            />
          </div>
        )
      },
      disableSortBy: true,
      width: 40,
    },
    {
      Header: 'ID',
      accessor: 'id',
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value ?? '-'}</span>
      },
    },
    {
      Header: 'ผู้ลงเอกสาร',
      accessor: 'createdByUser',
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value?.fullName ?? '-'}</span>
      },
    },
    {
      Header: 'ผู้ขาย',
      accessor: 'surveySaleUsers',
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {(value ?? []).map((item: any) => item?.user.fullName).join(', ') || '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่อัพเดทเอกสารล่าสุด',
      accessor: 'updatedAt',
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid()
              ? moment.utc(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm')
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'ชื่อหน่วยงาน',
      accessor: 'division',
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value ?? '-'}</span>
      },
    },
    {
      Header: 'ชื่อลูกค้า',
      accessor: 'customerTitle',
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value ?? '-'}</span>
      },
    },
    {
      Header: 'วันที่เริ่มงาน',
      accessor: 'startJobDate',
      Cell: ({ row }: any) => {
        return (
          <span className='text-gray-800'>
            {row?.original?.saleOrderId !== null &&
            moment(row?.original?.saleOrder?.startJobDate).isValid()
              ? moment(row?.original?.saleOrder?.startJobDate).format('DD/MM/YYYY')
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่สิ้นสุดงาน',
      accessor: 'endJobDate',
      Cell: ({ row }: any) => {
        return (
          <span className='text-gray-800'>
            {row?.original?.saleOrderId !== null &&
            moment(row?.original?.saleOrder?.endJobDate).isValid()
              ? moment(row?.original?.saleOrder?.endJobDate).format('DD/MM/YYYY')
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'สถานะงาน',
      accessor: 'jobStatus',
      Cell: ({ value, row }: any) => (
        <StatusCell status={value} isCreatedProject={row.original.isCreatedProject} />
      ),
    },
    {
      Header: 'สถานะการอนุมัติ',
      accessor: 'approvedAt',
      Cell: ({ row }: any) => (
        <div>
          {(row.original.approvedAt === null || row.original.approvedByUserId === null) && (
            <span className='badge badge-info' style={{ backgroundColor: '#6c757d' }}>
              รออนุมัติ
            </span>
          )}
          {row.original.approvedAt !== null && row.original.approvedResult === true && (
            <span className='badge badge-success'>อนุมัติแล้ว</span>
          )}
          {row.original.approvedAt !== null && row.original.approvedResult === false && (
            <div>
              <span className='badge badge-danger'>ไม่อนุมัติ</span>
              <div className='text-muted mt-2' style={{ fontSize: '0.9rem' }}>
                (เหตุผลที่ไม่อนุมัติ: {row.original.rejectReason})
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      Header: '',
      accessor: 'action',
      Cell: ({ row }: any) => (
        <ActionCell
          id={row.original.id}
          hideDuplicate
          hideCreateProject={
            ![1, 8, 11, 19].includes(userRoleId) ||
            row.original.approvedResult === false ||
            row.original.approvedResult === null ||
            row.original.approvedAt === null ||
            row.original.projects.length > 0
          }
          hideViewProject={row.original.projects.length === 0}
          hideApprove={
            ![1, 10].includes(userRoleId) ||
            (row.original.approvedAt !== null &&
              row.original.approvedByUserId !== null &&
              row.original.approveResult !== null)
          }
          hideReject={
            ![1, 10].includes(userRoleId) ||
            (row.original.approvedAt !== null &&
              row.original.approvedByUserId !== null &&
              row.original.approveResult !== null)
          }
          hideUploadApproverSignature={
            ![1, 10].includes(userRoleId) ||
            row.original.approvedAt === null ||
            row.original.approvedByUserId === null ||
            row.original.approvedResult === false ||
            row.original.approverSignaturePath !== null
          }
          hideDownloadApproverSignature={
            ![1, 10].includes(userRoleId) ||
            row.original.approvedAt === null ||
            row.original.approvedByUserId === null ||
            row.original.approvedResult === false ||
            row.original.approverSignaturePath === null
          }
          hideEdit={isHideWritingAction || [3, 4, 5].includes(row.original.jobStatusId)}
          hideDelete={isHideWritingAction || [3, 4, 5].includes(row.original.jobStatusId)}
          hideAssignSales={isHideWritingAction || [3, 4, 5].includes(row.original.jobStatusId)}
          hideEditNoteOther={![1, 7, 10, 19].includes(userRoleId)}
          onClickView={() => viewSurvey(row.original.id)}
          onClickEdit={() => editSurvey(row.original.id)}
          onClickDelete={() => askDeleteSurvey(row.original.id)}
          onAssignSales={() => onAssignSales(row.original.id)}
          onClickApprove={() => onApprove(row.original.id)}
          onClickReject={() => onReject(row.original.id)}
          onClickPrint={() => onDownloadPdf(row.original.id)}
          onDownloadApproverSignature={() => onDownloadApproverSignature(row.original.id)}
          onUploadApproverSignature={() => onUploadApproverSignature(row.original.id)}
          createProject={onStartCreateProject}
          viewProject={() => onViewProject(row.original.projects[0]?.id)}
          onEditNoteOther={() => onEditNoteOther(row.original.id)}
        />
      ),
      disableSortBy: true,
    },
  ]

  // Initial Fetch and Initial Table Setup
  useEffect(() => {
    const action = async () => {
      setupTable()
      await fetchData()
    }

    action()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchMasterData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isHideWritingAction,
    data,
    totalData,
    TABLE_CONFIGS,
    addSurvey,
    exportData,
    saleOrder,
    projects,
    filter,
    selectedSurveyId,
    isOpenAssignSalesModal,
    onChangeFilter,
    nextPageDesktop,
    submitFilter,
    clearFilter,
    onCancelAssignSales,
    onDeleteMultipleSurveys,
  }
}

export default ViewModel
